import { forwardRef, type SelectHTMLAttributes } from "react";

import { cn } from "./lib/cn.js";

/**
 * Select primitive.
 *
 * Canon: docs/design-canon/01-ui/forms.md R4 +
 *        docs/design-canon/01-ui/input-primitives.md.
 * Mirrors the <Input> shape: auto-wires `htmlFor`/`id`, surfaces
 * label + error inline, defaults to `min-w-0 w-full` so it shrinks
 * inside flex parents (per layout.md R1) and fills its column.
 *
 * Use this instead of bare `<select>` everywhere a labeled choice
 * is rendered. The `aria-label` escape hatch (filter dropdowns
 * without visible label) is still legitimate; for those, omit `label`
 * and pass `aria-label` directly.
 */
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, id, children, "aria-describedby": describedBy, ...props }, ref) => {
    // Announce the error to assistive tech (assessment U6).
    const errorId = id ? `${id}-error` : undefined;
    const describedByIds = [describedBy, error && errorId ? errorId : undefined]
      .filter(Boolean)
      .join(" ");
    return (
      <div className="space-y-1">
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-fg">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedByIds || undefined}
          className={cn(
            // min-w-0 mandatory inside flex parents (canon 01-ui/layout.md R1).
            "block w-full min-w-0 rounded-soft-md border bg-surface px-3 py-2 text-sm shadow-sm transition-colors",
            "focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand",
            "disabled:bg-surface-muted disabled:text-fg-subtle",
            error
              ? "border-danger-border-strong focus:border-danger-solid focus:ring-danger-solid"
              : "border-strong",
            className,
          )}
          {...props}
        >
          {children}
        </select>
        {error && (
          <p id={errorId} role="alert" className="text-xs text-danger-solid">
            {error}
          </p>
        )}
      </div>
    );
  },
);
Select.displayName = "Select";
